/**
 * Typed boundary over the JS-only lib/billing helpers. The JS defaults
 * (`= null`) cause TypeScript to infer overly narrow parameter types; this
 * module re-exports them with the signatures actually intended.
 */
import {
  analyzerOwnerForCycle as analyzerOwnerForCycleJs,
  findActiveAnalyzer as findActiveAnalyzerJs,
  productFromSubscription as productFromSubscriptionJs,
  selectionForCycle as selectionForCycleJs,
  subscriptionBillingCycle as subscriptionBillingCycleJs
} from 'lib/billing/selectionFromSubscription';
import { resolveFullCheckoutUpdates as resolveFullCheckoutUpdatesJs } from 'lib/billing/addonBilling';
import {
  pickActiveSubscription as pickActiveSubscriptionJs,
  pickManageableSubscription as pickManageableSubscriptionJs,
  summarizeSubscriptions as summarizeSubscriptionsJs
} from 'lib/billing/subscriptionPlan';
import { buildPriceCatalog as buildPriceCatalogJs } from 'lib/billing/priceCatalog';
import { computeLineAmountCents as computeLineAmountCentsJs, resolveCatalogPrice as resolveCatalogPriceJs } from 'lib/billing/priceCatalog';
import { addonEntitlementsFromAccount as addonEntitlementsFromAccountJs } from 'lib/billing/addonEntitlements';
import { extractDedicatedArrays as extractDedicatedArraysJs } from 'lib/billing/dedicatedSelection';
import { computePricing as computePricingJs } from '../Configure/pricing';
import type { BillingCatalog, BillingSubscription, Selection } from './types';

type Sub = BillingSubscription;
type Catalog = BillingCatalog | null;

interface AnalyzerOwner {
  subscription: Sub;
  subscriptionItemId: string | null;
  cycle: string;
  currentPeriodEnd: number | null;
  lineCount: number;
}

interface AnalyzerCycleOwner {
  subscription: Sub;
  subscriptionItemId: string | null;
  lineCount: number;
}

export const analyzerOwnerForCycle = analyzerOwnerForCycleJs as (
  subscriptions: Sub[],
  cycle: string,
  catalog: Catalog
) => AnalyzerCycleOwner | null;

export const findActiveAnalyzer = findActiveAnalyzerJs as (subscriptions: Sub[], catalog?: Catalog) => AnalyzerOwner | null;

export const productFromSubscription = productFromSubscriptionJs as (sub: Sub) => string | null;

export const subscriptionBillingCycle = subscriptionBillingCycleJs as (sub: Sub) => string;

export const pickActiveSubscription = pickActiveSubscriptionJs as (subscriptions: Sub[]) => Sub | null;

export const pickManageableSubscription = pickManageableSubscriptionJs as (subscriptions: Sub[]) => Sub | null;

export interface SubscriptionsSummary {
  activeSubs: Sub[];
  byProduct: Map<string, Sub>;
  hasMultiple: boolean;
  duplicateProducts: string[];
}

export const summarizeSubscriptions = summarizeSubscriptionsJs as (subscriptions: Sub[]) => SubscriptionsSummary;

export const buildPriceCatalog = buildPriceCatalogJs as (pricesResponse: unknown) => BillingCatalog;

export const computeLineAmountCents = computeLineAmountCentsJs as (price: unknown, qty: number) => number;

export const resolveCatalogPrice = resolveCatalogPriceJs as (
  catalog: Catalog,
  product: string,
  preferredCycle: string,
  options?: { subscriptions?: Sub[] }
) => unknown;

export interface AddonEntitlements {
  analyzer: boolean;
  dedicated: boolean;
  mcp?: boolean;
  [key: string]: unknown;
}

export const addonEntitlementsFromAccount = addonEntitlementsFromAccountJs as (
  addOns?: string[],
  subscriptions?: Sub[],
  catalog?: Catalog
) => AddonEntitlements;

export interface DedicatedArrays {
  envs: number[];
  subIds: Array<string | null>;
  subItemIds: Array<string | null>;
  nicknames: Array<string | null>;
  subLineCounts: number[];
  kinds: string[];
}

export const extractDedicatedArrays = extractDedicatedArraysJs as (selection: unknown) => DedicatedArrays;

export interface Pricing {
  monthlyCents: number;
  cycleCents: number;
  paidMonths: number;
  multiplier: number;
  [key: string]: unknown;
}

export const computePricing = computePricingJs as (
  selection: Selection,
  catalog: BillingCatalog | null,
  // The JS impl has always taken this; the shim just never declared it.
  trialUsedProductIds?: string[]
) => Pricing;

export const selectionForCycle = selectionForCycleJs as (subscriptions: Sub[], cycle: string, catalog: Catalog) => Partial<Selection>;

export const resolveFullCheckoutUpdates = resolveFullCheckoutUpdatesJs as (
  selection: Selection,
  catalog: BillingCatalog | null,
  subscriptions: Sub[],
  currentAddons: AddonEntitlements,
  trialContext?: { trialUsedProductIds?: string[]; skipTrial?: boolean } | null
) => { type?: string; ops?: Array<Record<string, unknown>> } | null;
